import { Box, Link, Stack, Typography } from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router"
import { Bound } from "lib/@components/binding/Bound"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { NoResults } from "lib/@components/no-results/no-results"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useProject } from "authoring/lib/hooks/use-project"
import { getMaintenanceTask, getMaintenanceTaskHistory } from "routes/component-library/controller/maintenance-library"
import { getComponentSimilarity } from "../controller/get-component-similarity"
import { refreshOnlyFor } from "lib/graphql/api"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { uniqueId } from "library/database/split-id"
import { AuthoringTaskList } from "./authoring-task-list"
import { TaskSimilarityItem } from "./task-similarity-item"
import { SimilarityCheckboxHeader, similarityTableHeaders } from "./lib/similarity-table-headers"
import { SelectAllSimilarityResults } from "./components/select-all-similarity-results"
import { BaseComponentCard } from "./base-component-card"
import { SimilaritySummaryCard } from "./similarity-summary-card"
import { SimilarityAvailabilityFilter } from "./similarity-availability-filter"
import { buildBaseComponent, selectComponentVersion } from "./component-task-utils"
import { AddComponentTasksButton } from "./components/add-component-tasks-button"
import { TreeToolbar } from "slot-definitions"
import { navigateGroup, navigateProject } from "authoring/routes/project/utilities/project-navigation"
import { hasDemand } from "lib/authorization/has-demand"
import { AUTHORING_EDIT_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { SimilaritySelectionProvider } from "./similarity-selection-provider"
import { useSkills } from "routes/@lib/skills"

const PAGE_SIZE = 20
const EMPTY_IDS = []

function haveSameMembers(a = [], b = []) {
    if (a.length !== b.length) return false
    const set = new Set(a)
    return b.every((id) => set.has(id))
}

export function ComponentSimilarityPanel() {
    const { projectId, groupId, componentId, componentVersion } = useParams()
    const [showAvailable, setShowAvailable] = useState(true)
    const [showLocked, setShowLocked] = useState(true)
    const [skip, setSkip] = useState(0)
    const [take, setTake] = useState(PAGE_SIZE)
    const [search] = useSearchParam("search", "")
    const { loading: projectLoading, project } = useProject({ id: projectId })
    const canEditSchedule = hasDemand(AUTHORING_EDIT_SCHEDULES_DEMAND)
    const currentComponent = getMaintenanceTask.useResults(componentId)
    const componentHistory = getMaintenanceTaskHistory.useResults(componentId)
    const component = useMemo(selectVersion, [currentComponent, componentHistory, componentVersion])
    const [skillsIndex] = useSkills()
    const sourceTask = useMemo(() => buildBaseComponent(component, skillsIndex), [component, skillsIndex])
    const query = useMemo(buildQuery, [sourceTask, projectId, search, showAvailable, showLocked, skip, take])
    // Only re-fetch when a schedule shown in THIS result set is scoped/unscoped elsewhere,
    // so one author's work never refreshes another author's list.
    const [lockWatchIds, setLockWatchIds] = useState(EMPTY_IDS)
    const lockWatch = useMemo(() => refreshOnlyFor(lockWatchIds), [lockWatchIds])
    const data = getComponentSimilarity.useResults(query, lockWatch, withInitialValue(IS_LOADING))
    const resolvedData = data && data !== IS_LOADING ? data : null
    const lastData = useRef(null)
    if (resolvedData) lastData.current = resolvedData
    const {
        items = [],
        totalCount = 0,
        availableCount = 0,
        lockedCount = 0,
        availableScheduleTaskCounts = [],
    } = resolvedData ?? lastData.current ?? {}
    useEffect(syncLockWatchIds, [items])
    const refreshKey = `${search}:${showAvailable}:${showLocked}:${take}`
    const selectionKey = `${projectId}:${componentId}:${componentVersion}`
    const changePageSize = useCallback((value) => {
        setSkip(0)
        setTake(value)
    }, [])
    const waitingForSelectedVersion =
        currentComponent && Number(componentVersion) !== currentComponent.version && componentHistory === undefined

    TreeToolbar.usePlug(<AddComponentTasksButton />)
    SimilarityCheckboxHeader.usePlug(<SelectAllSimilarityResults />)

    if (projectLoading) return <LoadingScreen />
    if (!project) return <NoResults />
    const group = project.groups?.find(({ id }) => id === groupId)
    if (!canEditSchedule || group?.readOnly) return navigateGroup({ projectId, groupId })
    if (currentComponent === undefined || waitingForSelectedVersion) return <LoadingScreen />
    if (!component || !sourceTask) return <NoResults>Component version not found</NoResults>

    return (
        <SimilaritySelectionProvider
            key={selectionKey}
            availableCount={showAvailable ? availableCount : 0}
            availableScheduleTaskCounts={showAvailable ? availableScheduleTaskCounts : []}
            search={search}
        >
            <Bound
                project={project}
                groupId={groupId}
                sourceTask={sourceTask}
                search={search}
                usesServerSelection
                isSimilarResults
            >
                <PageBreadcrumbContainer
                    title="Find Similar Tasks"
                    parentCrumbs={[
                        <Link key="project" sx={{ cursor: "pointer" }} onClick={() => navigateProject(projectId)}>
                            {project.name}
                        </Link>,
                        <Link
                            key="group"
                            sx={{ cursor: "pointer" }}
                            onClick={() => navigateGroup({ projectId, groupId })}
                        >
                            {group?.name}
                        </Link>,
                    ]}
                >
                    <Box flex={1}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2, mb: 3 }}>
                            <Box sx={{ width: { xs: 1, md: "50%" }, display: "flex" }}>
                                <BaseComponentCard component={sourceTask} sx={{ flex: 1 }} />
                            </Box>
                            <Box sx={{ width: { xs: 1, md: "50%" }, display: "flex" }}>
                                <SimilaritySummaryCard
                                    availableCount={availableCount}
                                    lockedCount={lockedCount}
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                        </Stack>
                        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ lg: "center" }} mb={2}>
                            <Typography variant="h6" sx={{ flex: 1 }} data-cy="similar-tasks-heading">
                                Similar Tasks
                            </Typography>
                            <SearchBoxUsingSearchParam
                                placeholder="Search by task name, schedule title or schedule code..."
                                sx={{ width: { xs: 1, lg: 500 }, m: 0 }}
                            />
                            <SimilarityAvailabilityFilter
                                showAvailable={showAvailable}
                                showLocked={showLocked}
                                onShowAvailable={setShowAvailable}
                                onShowLocked={setShowLocked}
                            />
                        </Stack>
                        <AuthoringTaskList
                            tasks={items}
                            itemComponent={<TaskSimilarityItem />}
                            tableHeaders={similarityTableHeaders}
                            sx={{ tableLayout: "fixed" }}
                            loading={!resolvedData && !lastData.current}
                            requestPageRepeatProps={{
                                total: totalCount,
                                pageSize: take,
                                setSkip,
                                setTake,
                                onPageSizeChange: changePageSize,
                                refreshKey,
                            }}
                        />
                    </Box>
                </PageBreadcrumbContainer>
            </Bound>
        </SimilaritySelectionProvider>
    )

    function selectVersion() {
        return selectComponentVersion({
            component: currentComponent,
            history: componentHistory,
            version: componentVersion,
        })
    }

    function buildQuery() {
        if (!sourceTask) return undefined
        return {
            projectId,
            title: sourceTask.title,
            content: sourceTask.content,
            search,
            showAvailable,
            showLocked,
            skip,
            take,
        }
    }

    function syncLockWatchIds() {
        const ids = [...new Set(items.map((item) => uniqueId(item.scheduleId)).filter(Boolean))]
        setLockWatchIds((previous) => (haveSameMembers(previous, ids) ? previous : ids))
    }
}
